import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Dialog, DialogTitle, IconButton, Tooltip, Box, Avatar } from '@mui/material';
import { Delete, Restore } from '@mui/icons-material';

import { authContext } from '../../routes';
import axiosInstance from '../../api';
import Label from '../../components/label';
import CustomSnackbar from '../../components/alerts/CustomSnackbar';
import SERVER_API_URL from '../../config';
import RestoreDialog from '../common/RestoreDialog';
import DeleteDialog from '../common/PermanentDeleteDialog';
import { ethDate } from '../../utils/ethDate';
import DeletedUserDataGrid from '../common/DeletedUserDataGrid';

function AvatarCellRenderer(params) {
  const [openImage, setOpenImage] = useState(false);
  const { id } = params;
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const getAvatar = async () => {
      try {
        const response = await axiosInstance.get(`${SERVER_API_URL}/avatar/getAvatarById/${id}`, {
          responseType: 'blob',
        });
        if (response.status === 200) {
          const url = URL.createObjectURL(response.data);
          setAvatarUrl(url);
        }
      } catch (e) {
        setAvatarUrl(null);
      }
    };
    const getUserData = async () => {
      try {
        const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${id}`);
        if (response.status === 200) {
          setUserData(response.data);
        }
      } catch (error) {
        // console.log(error)
      }
    };
    getAvatar();
    getUserData();
  }, [id, setAvatarUrl, setUserData]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={`መለያ`} arrow>
          <IconButton onClick={() => setOpenImage(true)}>
            {<Avatar src={avatarUrl} alt={`${userData && userData.fullName} መገለጫ`} />}
          </IconButton>
        </Tooltip>
      </Box>
      <Dialog open={openImage} onClose={() => setOpenImage(false)}>
        {avatarUrl && <img src={avatarUrl} alt={`${userData && userData.fullName} መገለጫ`} />}
        {!avatarUrl && <img src="/assets/icon/empty_user_profile.png" alt={`${userData && userData.firstName} መገለጫ`} />}
        <DialogTitle>{userData && userData.fullName}</DialogTitle>
      </Dialog>
    </>
  );
}

export default function DeletedUserList() {
  const { user } = useContext(authContext);
  const [userList, setUserList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openRestore, setOpenRestore] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const [notification, setNotification] = useState({});

  const columns = [
    {
      headerClassName: 'super-app-theme--header', field: 'id',
      width: 100,
      headerName: 'መለያ',
      renderCell: AvatarCellRenderer,
    },
    {
      headerClassName: 'super-app-theme--header', field: 'fullName',
      width: 300,
      headerName: 'ሙሉ ስም',
    },
    { headerClassName: 'super-app-theme--header', field: 'christianName', width: 200, headerName: 'የክርስትና ስም' },
    {
      headerClassName: 'super-app-theme--header', field: 'phone',
      width: 150,
      headerName: 'ስልክ ቁጥር',
      renderCell: (params) => (
        <>
          {!params.value && <Label sx={{ color: 'maroon' }}>ባዶ</Label>}
          {params.value}
        </>
      ),
    },
    { headerClassName: 'super-app-theme--header', field: 'gender', width: 100, headerName: 'ጾታ' },
    {
      headerClassName: 'super-app-theme--header', field: 'status',
      width: 100,
      headerName: 'ሁኔታ',
    },
    {
      headerClassName: 'super-app-theme--header', field: 'actions',
      width: 150,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleDelete = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedUser(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        const handleRestore = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/user/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedUser(response.data);
              setOpenRestore(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        return (
          <>
            <Tooltip title="ፋይሉን መልስ" color="success" arrow>
              <IconButton disabled={!(user && user.role.permissions.user.delete)} onClick={handleRestore}>
                <Restore sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton onClick={handleDelete} disabled={!(user && user.role.permissions.user.delete)}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'church', width: 200, headerName: 'አድባር ስም',
      valueGetter: (params) => {
        const { id, name } = params.row.church;
        if (id.length <= name.length)
          return name.slice(0, name.length - id.length);
        return name;
      },
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'district', width: 200, headerName: 'ወረዳ', 
      valueGetter: (params) => {
        const { id, name } = params.row.church.district;
        if (id.length <= name.length)
          return name.slice(0, name.length - id.length);
        return name;
      },
    }, 
    {
      headerClassName: 'super-app-theme--header', field: 'role',
      width: 150,
      headerName: 'ሚና',
      valueGetter: (params) => params.row.role.name,
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'category',
      width: 100,
      headerName: 'ምድብ',
      valueGetter: (params) => params.row.category.name,
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'position',
      width: 100,
      headerName: 'የቤተክርስትያን የስራ መደብ',
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'lastLoggedIn',
      headerName: 'መጨረሻ የገባበት ቀን',
      width: 180,
      renderCell: (params) => (
        <>
          {!params.value && <Label sx={{ color: 'maroon' }}>እስካሁን አልገባም።</Label>}
          {params.value && ethDate(params.value)}
        </>
      ),
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'createdAt',
      headerName: 'የተመዘገበበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header',
      field: 'updatedAt',
      headerName: 'የተስተካከለበት ቀን',
      width: 180,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleDeleteOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/user/deleteOneById/${selectedUser.id}`);
      if (response.status === 200) {
        getAllUser();
        setOpenDelete(false);
      }
    } catch (error) {
      // console.log(error)
    }
  };

  const handleRestoreOk = async () => {
    try {
      const response = await axiosInstance.patch(`${SERVER_API_URL}/user/restoreOneById/${selectedUser.id}`);
      if (response.status === 200) {
        getAllUser();
      }
    } catch (error) {
      if (error.response.status === 406) {
        notification.message = error.response.message;
        notification.severity = 'error';
      }
    }
    setOpenRestore(false);
  };

  const getAllUser = useCallback(async () => {
    try {
      let response;
      if (user && user.role.permissions.dioceseLevel.read) {
        response = await axiosInstance.post(`${SERVER_API_URL}/user/findAllDeleted`);
      } else if (user && user.role.permissions.districtLevel.read) {
        response = await axiosInstance.post(
          `${SERVER_API_URL}/user/findManyDeletedByDistrictId/${user && user.church.district.id}`
        );
      } else {
        response = await axiosInstance.post(
          `${SERVER_API_URL}/user/findManyDeletedByChurchId/${user && user.church.id}`
        );
      }

      if (response.status === 200) {
        const rawUsers = response.data.filter((person) => person.id !== (user && user.id));
        setUserList(rawUsers);
      }
    } catch (error) {
      // console.log(error);
    }
  }, [user]);

  useEffect(() => {
    getAllUser();
  }, [getAllUser]);

  return (
    <>
      {notification.message && <CustomSnackbar notification={notification} setNotification={setNotification} />}

      <DeletedUserDataGrid columns={columns} userList={userList} />

      {selectedUser && (
        <DeleteDialog
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
          nameToBeDeleted={selectedUser.fullName}
          nameType="የተክታይ"
          message="መረጃ"
          handleOk={handleDeleteOk}
          id={selectedUser.id}
        />
      )}

      {selectedUser && (
        <RestoreDialog
          openRestore={openRestore}
          setOpenRestore={setOpenRestore}
          nameToBeRestored={selectedUser.fullName}
          nameType="የተክታይ"
          handleRestoreOk={handleRestoreOk}
          id={selectedUser.id}
        />
      )}
    </>
  );
}
